import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Method,
  type EventEmitter,
  Event,
} from '@stencil/core';
import { isEnterKey, renderResponsiveImages } from '../../helpers/helpers';
import type { ResponsiveImages } from '../../helpers/utils/interfaces';
import { trackEvent } from '../../helpers/tracking';

@Component({
  tag: 'cms-overlay',
  styleUrl: 'cms-overlay.scss',
  shadow: true,
})
export class CmsOverlay {
  private readonly overlayImageSizes = `
    (max-width: 767px) and (min-resolution: 288dpi) 33vw,
    (max-width: 767px) and (min-resolution: 192dpi) 50vw,
    (max-width: 767px) and (min-resolution: 96dpi) 100vw,
    (min-width: 768px) 50vw,
    100vw`;

  private overlayContainer: HTMLElement;
  private bodyNode: HTMLElement;

  @Element() hostElement: HTMLElement;

  /**
   * set the overlay state
   */
  @Prop({ mutable: true }) open = false;

  /**
   * set the overlay headline
   */
  @Prop({ mutable: true }) headline: string;

  /**
   * set the image alt text
   */
  @Prop({ mutable: true }) imageAltText = 'Hornbach';

  /**
   * set the image
   */
  @Prop({ mutable: true }) responsiveImages: ResponsiveImages;

  /**
   * set the slot-name for the content
   */
  @Prop({ mutable: true }) contentSlot: string;

  /**
   * set the slot-name for the content
   */
  @Prop({ mutable: true }) imageSlot?: string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  componentWillLoad() {
    this.bodyNode = document.querySelector('body');
  }

  componentDidLoad() {
    this.overlayContainer = this.hostElement;
    window.addEventListener('keydown', (event) => {
      if (this.open && event.key === 'Escape') {
        this.closeOverlay();
      }
    });
  }

  private renderOverlayContainer() {
    return (
      <div class={'overlay-container-inner'}>
        {this.renderOverlayResponsiveImages()}
        {this.renderOverlayContentContainer()}
      </div>
    );
  }

  private renderOverlayResponsiveImages() {
    return (
      <button
        type='button'
        onClick={() => {
          this.toggleOverlay();
        }}
        onKeyDown={(event) => {
          if (isEnterKey(event)) {
            this.toggleOverlay();
          }
        }}
        class={'overlay-image-container'}
      >
        <div class={'overlay-image-wrapper'}>
          <div class='overlay-image'>
            {this.responsiveImages != null
              ? renderResponsiveImages(
                  this.responsiveImages,
                  this.overlayImageSizes,
                  this.imageAltText,
                  'infographic-overlay-images',
                )
              : ''}
            {this.imageSlot ? <slot name={this.imageSlot} /> : ''}
          </div>
        </div>
      </button>
    );
  }

  private renderOverlayContentContainer() {
    return (
      <div class={'overlay-content-container'}>
        {this.renderOverlayHeader()}
        <div class={'separator'} />
        <div class='overlay-content-wrapper'>
          <slot name={this.contentSlot} />
        </div>
      </div>
    );
  }

  private renderOverlayHeader() {
    return (
      <div class={'overlay-header'}>
        <div class={'overlay-headline-container'} innerHTML={this.headline} />
        <cms-button
          theme='flat'
          icon='close'
          iconSize='md'
          iconColor='--cms-semanticColors-base-strong-icon'
          size='button-lg'
          class='overlay-close-container'
          namespace={`${this.namespace}-close`}
          onCmsButtonClick={() => {
            this.closeOverlay();
          }}
        />
      </div>
    );
  }

  @Method()
  async toggleOverlay() {
    if (this.open) {
      this.closeOverlay();
    } else {
      this.openOverlay();
    }
  }

  @Method()
  async openOverlay() {
    if (this.open) return;
    if (this.bodyNode !== undefined) {
      this.bodyNode.style.overflow = 'hidden';
    }

    if (this.overlayContainer !== undefined) {
      this.overlayContainer.style.width = `${100}%`;
      this.overlayContainer.style.overflowY = 'visible';
      this.overlayContainer.style.zIndex = '1000';
    }
    /*
      Track Event
    */
    trackEvent(`${this.namespace}-open`);

    this.open = true;
  }

  @Method()
  async closeOverlay() {
    if (this.bodyNode !== undefined) {
      this.bodyNode.style.overflow = 'auto';
    }

    if (this.overlayContainer !== undefined) {
      this.overlayContainer.style.width = `${0}%`;
    }

    this.open = false;
  }

  @Event({
    eventName: 'cmsOverlayOpen',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  cmsOverlayOpen: EventEmitter<void>;

  @Event({
    eventName: 'cmsOverlayClose',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  cmsOverlayClose: EventEmitter<void>;

  render() {
    return (
      <Host
        class={{ 'overlay-container': true, open: this.open }}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.closeOverlay();
          }
        }}
      >
        {this.open ? this.renderOverlayContainer() : ''}
      </Host>
    );
  }
}
